import { Stack, TextField } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { useCallback, useMemo, useState } from 'react';
import { useChartsTheme } from '@/components';
import { TableThresholdOptions, TableThresholdStepOption } from '@/core';
import {
  ThresholdsEditor,
  ThresholdsEditorProps,
} from '@/plugin-editor-components/components/ThresholdsEditor/NewThresholdsEditor';
import { TextAndBackgroundColorPicker } from '@/plugin-editor-components/components/ThresholdsEditor/TextAndBackgroundColorPicker';

export function useDefaultPaletteForTableThresholds() {
  const chartsTheme = useChartsTheme();

  return useMemo(() => {
    return {
      defaultColor: chartsTheme.theme.palette.text.primary,
      palette: [],
    };
  }, [chartsTheme]);
}

type TableThresholdsEditorProps = Pick<
  ThresholdsEditorProps<TableThresholdStepOption, TableThresholdOptions>,
  'thresholds' | 'onChange'
>;

export const TableThresholdsEditor = ({ thresholds, onChange }: TableThresholdsEditorProps) => {
  const defaultPalette = useDefaultPaletteForTableThresholds();

  return (
    <ThresholdsEditor
      hideThresholdMode
      thresholds={thresholds}
      onChange={onChange}
      renderStepOptions={({ step, onStepChange }) => {
        return <TableThresholdStepEditor step={step} onStepChange={onStepChange} />;
      }}
      renderColorPicker={TableThresholdColorPicker}
      defaultPalette={defaultPalette}
    />
  );
};

interface TableThresholdStepEditorProps {
  step: Omit<TableThresholdStepOption, 'value'>;
  onStepChange: (newStep: Omit<TableThresholdStepOption, 'value'>) => void;
}

function TableThresholdStepEditor({ step, onStepChange }: TableThresholdStepEditorProps) {
  const [message, setMessage] = useState(step.name ?? '');

  // Sync local edits when `step.name` changes externally (reset, undo, spec edit).
  const [prevName, setPrevName] = useState(step.name);
  if (step.name !== prevName) {
    setPrevName(step.name);
    setMessage(step.name ?? '');
  }

  const handleMessageSubmit = () => {
    onStepChange(
      produce(step, (draft) => {
        draft.name = message;

        if (draft.name === '') {
          delete draft.name;
        }
      })
    );
  };

  return (
    <Stack>
      <TextField
        fullWidth
        label="Message"
        value={message}
        onChange={(e) => setMessage(e.currentTarget.value)}
        onBlur={handleMessageSubmit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleMessageSubmit();
          }
        }}
      />
    </Stack>
  );
}

interface TableThresholdColorPickerProps extends TableThresholdStepEditorProps {
  defaultColor: string;
}

function TableThresholdColorPicker({ step, onStepChange, defaultColor }: TableThresholdColorPickerProps) {
  const handleColorChange = useCallback(
    ({ color, background }: { color?: string; background?: string }) => {
      onStepChange(
        produce(step, (draft) => {
          draft.color = color;
          draft.background_color = background;

          if (draft.color === undefined) {
            delete draft.color;
          }
          if (draft.background_color === undefined) {
            delete draft.background_color;
          }
        })
      );
    },
    [step, onStepChange]
  );

  return (
    <TextAndBackgroundColorPicker
      onColorChange={handleColorChange}
      color={{ color: step.color || defaultColor, background: step.background_color }}
    />
  );
}
